import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Languages, FileStack } from "lucide-react";
import { useApi, useFetch, errMsg } from "../lib/api";
import { Button } from "./ui/button";

export const LANGS = [["en", "English"], ["hi", "हिन्दी"], ["gu", "ગુજરાતી"], ["mr", "मराठी"], ["te", "తెలుగు"]];
const primary = "bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold";

export const DefaultLanguage = ({ vendors, locked, onDone }) => {
  const api = useApi();
  const [vid, setVid] = useState("");
  const [lang, setLang] = useState("hi");
  const vendorId = locked || vid;
  const current = (vendors || []).find((v) => v.id === vendorId)?.default_language;
  const apply = async () => {
    try { const r = (await api.post("/workers/default-language", { vendor_id: vendorId || null, lang })).data; toast.success(`Default saved. ${r.updated} worker(s) without a language now use it`); onDone?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="c-surface p-4 c-rise" data-testid="default-language-card">
      <div className="flex items-center gap-2 font-semibold text-sm mb-1"><Languages size={15} className="text-[#00E599]" />Contractor default print language</div>
      <p className="text-xs text-slate-500 mb-3">Fills in workers who have no language set. Workers with their own language keep it.</p>
      <div className="flex flex-wrap gap-2 items-end">
        {!locked && <select className="c-input !w-auto" value={vid} onChange={(e) => setVid(e.target.value)} data-testid="default-language-vendor">
          <option value="">Select contractor…</option>{(vendors || []).map((v) => <option key={v.id} value={v.id}>{v.vendor_name}</option>)}</select>}
        <select className="c-input !w-auto" value={lang} onChange={(e) => setLang(e.target.value)} data-testid="default-language-lang" translate="no">
          {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        <Button onClick={apply} disabled={!vendorId} className={primary} data-testid="default-language-apply">Apply to workers</Button>
      </div>
      {current && <p className="text-[11px] text-slate-400 mt-2" data-testid="default-language-current">Current default: {LANGS.find(([v]) => v === current)?.[1]}</p>}
    </div>);
};

export const BulkOfferLetters = ({ workers, month, siteId, buildHref }) => {
  const joiners = (workers || []).filter((w) => (w.date_of_joining || "").startsWith(month) && (!siteId || w.site_id === siteId));
  const [sel, setSel] = useState([]);
  const [kit, setKit] = useState(true);
  useEffect(() => { setSel(joiners.map((w) => w.id)); }, [month, workers, siteId]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  return (
    <div className="c-surface p-4 c-rise" data-testid="bulk-offer-card">
      <div className="flex items-center gap-2 font-semibold text-sm mb-1"><FileStack size={15} className="text-[#00E599]" />Bulk offer letters: new joiners</div>
      <p className="text-xs text-slate-500 mb-3">Workers who joined in the chosen month. Untick anyone you don't need, then print all as one PDF.</p>
      <div className="grid gap-1.5 max-h-48 overflow-y-auto mb-3" data-testid="bulk-offer-list">
        {joiners.map((w) => (
          <label key={w.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid="bulk-offer-row">
            <input type="checkbox" checked={sel.includes(w.id)} onChange={() => toggle(w.id)} data-testid={`bulk-offer-check-${w.id}`} className="accent-[#00E599]" />
            <span>{w.worker_full_name}</span><span className="text-[11px] text-slate-500 font-mono">{w.date_of_joining} · {w.vendor_name}</span></label>))}
        {!joiners.length && <p className="text-xs text-slate-500" data-testid="bulk-offer-empty">No workers joined in this month.</p>}
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-300 mb-3 cursor-pointer">
        <input type="checkbox" checked={kit} onChange={(e) => setKit(e.target.checked)} data-testid="bulk-offer-kit-toggle" className="accent-[#00E599]" />
        Full welcome kit: add EPF Form 2 and ESIC Form 1 after each offer letter</label>
      <Button asChild={sel.length > 0} disabled={!sel.length} className={primary} data-testid="bulk-offer-open-btn">
        {sel.length ? <a href={buildHref(sel, kit)} target="_blank" rel="noreferrer">Open {sel.length} {kit ? "welcome kit(s)" : "offer letter(s)"}</a> : <span>Select workers</span>}

      </Button>
    </div>);
};

export const LanguageCoverage = ({ tick }) => {
  const { data, reload } = useFetch("/workers-coverage/languages");
  useEffect(() => { if (tick) reload(); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps
  const name = (v) => LANGS.find(([k]) => k === v)?.[1] || v;
  return (
    <div className="c-surface p-4 c-rise lg:col-span-2" data-testid="language-coverage-card">
      <div className="flex items-center gap-2 font-semibold text-sm mb-1"><Languages size={15} className="text-[#00E599]" />Print language coverage</div>
      <p className="text-xs text-slate-500 mb-3">Active workers per contractor who still have no print language of their own.</p>
      <div className="overflow-x-auto"><table className="c-table w-full" translate="no">
        <thead><tr><th>Contractor</th><th>Workers</th><th>No language</th><th>Contractor default</th><th>Languages set</th></tr></thead>
        <tbody>{(data || []).map((r) => (
          <tr key={r.vendor_id} data-testid="language-coverage-row">
            <td className="text-sm">{r.vendor_name}</td><td className="text-sm font-mono">{r.total}</td>
            <td className="text-sm font-mono" data-testid="language-coverage-missing"><span className={r.missing ? "text-amber-400" : "text-emerald-400"}>{r.missing}</span>
              {r.missing > 0 && r.default_language && <span className="text-[11px] text-slate-500 font-sans"> · print in {name(r.default_language)}</span>}</td>
            <td className="text-sm">{r.default_language ? name(r.default_language) : "—"}</td>
            <td className="text-xs text-slate-400">{Object.entries(r.by_lang).map(([k, n]) => `${name(k)} ${n}`).join(" · ") || "—"}</td>
          </tr>))}</tbody></table></div>
      {data?.length === 0 && <p className="text-xs text-slate-500 mt-2">No contractors yet.</p>}
    </div>);
};
